import React, { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import { View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { CHECKLIST_PURPOSE } from '../../../common/constants';
import { CHECKLIST_ITEM_TYPES } from '../../../common/utils/checklist/checklistItems';
import {
	CHECKLIST_SIGN_OFF_STATE,
	formatChecklistSignOffTime,
	getChecklistSignOff,
} from '../../../common/utils/checklist/checklistSignOff';
import { useUserContext } from '../../../common/utils/context';
import { getUserName } from '../../../common/utils/user/getUserName';

import { Text } from '../../components';
import {
	ActionDock,
	Card,
	EmptyState,
	Screen,
	SectionTitle,
} from '../../design/components';
import useBackLabel from '../../hooks/useBackLabel';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import withMediaPicker from '../../hocs/withMediaPicker';
import { useOfflineQueue } from '../../offline';
import { useTheme } from '../../theme';
import StepCount from '../driver/endOfDay/StepCount';
import ChecklistItem from './ChecklistItem';
import { createChecklistFormViewStyles } from './ChecklistFormView.styles';
import { MAX_PHOTOS_PER_ITEM, isItemAnswered } from './checklistFormHelpers';
import ManagerSignOffSelect from './ManagerSignOffSelect';
import useChecklistForm from './useChecklistForm';

const PURPOSE_COPY = {
	[CHECKLIST_PURPOSE.LEAVE_WAREHOUSE]: {
		subheading: 'Fill this out right before you leave the warehouse.',
		submitLabel: 'Submit and head out',
	},
	[CHECKLIST_PURPOSE.VEHICLE_END_OF_DAY]: {
		subheading: 'Check each item before you finish for the day.',
		signOffSubheading: 'Check each item, then get your van signed off by a manager.',
	},
	[CHECKLIST_PURPOSE.SELF_DEBRIEF]: {
		subheading: 'Debrief with your manager, or fill this in if they are not around.',
		submitLabel: 'Submit debrief',
	},
};

const ChecklistFormView = ({
	route,
	captureMedia,
	capturedMediaBatch,
	processingMedia,
}) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createChecklistFormViewStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const { user, tenant } = useUserContext();
	const { isOnline } = useOfflineQueue();
	const {
		purpose,
		serviceRunId,
		vehicleId,
		fallbackTitle,
		stepPosition,
	} = route?.params || {};

	const {
		template,
		loading,
		error,
		refetch,
		isCheckingSubmission,
		submitted,
		visibleItems,
		sections,
		isVehicleCheck,
		requiresManagerSignOff,
		answers,
		comments,
		validationErrors,
		flaggedItemIds,
		missingRequiredItems,
		requiredCount,
		managerSignOff,
		setManagerSignOff,
		managerSignOffError,
		setManagerSignOffError,
		isSubmitting,
		activePhotoItemIdRef,
		setAnswerValue,
		setCommentValue,
		startPhotoCapture,
		removePhoto,
		handleSubmit,
	} = useChecklistForm({ purpose, serviceRunId, vehicleId, captureMedia, capturedMediaBatch });

	const backLabel = useBackLabel();
	const goBack = useCallback(() => navigation.goBack(), [navigation]);

	if (!template || isCheckingSubmission) {
		const isLoadingTemplate = Boolean(purpose) && (loading || isCheckingSubmission);
		return (
			<Screen
				backLabel={backLabel}
				headerRight={<StepCount position={stepPosition} />}
				heading={isLoadingTemplate || error ? undefined : fallbackTitle || 'Checklist'}
				isLoading={isLoadingTemplate}
				error={!isLoadingTemplate && error ? 'The checklist could not be loaded. Check your connection and try again.' : undefined}
				onRetry={() => refetch()}
			>
				<Card>
					<EmptyState
						title="Not configured"
						message="No checklist is configured for your state yet."
						actionLabel="Go back"
						onAction={goBack}
					/>
				</Card>
			</Screen>
		);
	}

	const purposeCopy = PURPOSE_COPY[purpose] || {};
	const submitLabel = isVehicleCheck && requiresManagerSignOff
		? 'Complete vehicle check'
		: purposeCopy.submitLabel || 'Submit checklist';
	const isLocked = missingRequiredItems.length > 0 || (requiresManagerSignOff && !managerSignOff);
	const firstErrorItem = visibleItems.find(item => validationErrors[item.id]);
	let dockHint;
	if (missingRequiredItems.length) {
		dockHint = missingRequiredItems.length === requiredCount
			? `Answer all ${requiredCount} required ${requiredCount === 1 ? 'item' : 'items'} to submit`
			: `Answer ${missingRequiredItems.length} more required ${missingRequiredItems.length === 1 ? 'item' : 'items'} to submit`;
	}
	else if (requiresManagerSignOff && !managerSignOff) {
		dockHint = 'Select the manager who will sign off';
	}
	else if (firstErrorItem) {
		dockHint = `${firstErrorItem.label}: ${validationErrors[firstErrorItem.id]}`;
	}
	else if (!isOnline) {
		dockHint = 'You are offline. This is saved on the phone and syncs later.';
	}

	// The selected manager signs off later on their phone; a queued checklist asks them once it syncs.
	const signOff = submitted && !submitted.isQueued ? getChecklistSignOff(submitted, tenant?.timezone) : null;
	let submittedHint;
	if (submitted?.isQueued) {
		submittedHint = 'Saved on the phone, will sync. It can\'t be changed.';
	}
	else if (signOff) {
		// The sign-off card sits below the answers, so the dock repeats where sign-off stands.
		submittedHint = `${signOff.text}. It can't be changed.`;
	}
	else if (submitted) {
		submittedHint = submitted.submittedAt
			? `Submitted at ${formatChecklistSignOffTime(tenant?.timezone, submitted.submittedAt)}. It can't be changed.`
			: 'Submitted. It can\'t be changed.';
	}
	const isSignedOff = signOff?.state === CHECKLIST_SIGN_OFF_STATE.SIGNED_OFF;
	const isWaitingForSignOff = signOff?.state === CHECKLIST_SIGN_OFF_STATE.WAITING ||
		Boolean(submitted?.isQueued && submitted.hasManagerSignOff);
	let signOffLabel = 'Not signed off yet';
	if (signOff) {
		signOffLabel = signOff.text;
	}
	else if (submitted?.isQueued && submitted.hasManagerSignOff) {
		signOffLabel = 'Your manager is asked to sign off once this syncs';
	}
	else if (!submitted && managerSignOff) {
		signOffLabel = `${getUserName(managerSignOff)} will be asked to sign off`;
	}
	let signOffIcon = 'circle';
	let signOffIconColor = colors.iconMuted;
	if (isSignedOff) {
		signOffIcon = 'check-circle';
		signOffIconColor = colors.success;
	}
	else if (isWaitingForSignOff) {
		signOffIcon = 'clock';
		signOffIconColor = colors.warning;
	}
	// A submitted checklist shows its saved answers greyed and cannot be changed.
	const readOnlyProps = submitted
		? { pointerEvents: 'none', accessibilityState: { disabled: true } }
		: {};
	const sectionStyle = [styles.section, Boolean(submitted) && styles.readOnly];

	return (
		<Screen
			backLabel={backLabel}
			headerRight={<StepCount position={stepPosition} />}
			heading={template.title || fallbackTitle || 'Checklist'}
			subheading={requiresManagerSignOff && purposeCopy.signOffSubheading ? purposeCopy.signOffSubheading : purposeCopy.subheading}
			dock={submitted ? <ActionDock hint={submittedHint} /> : (
				<ActionDock
					hint={dockHint}
					action={{
						label: submitLabel,
						onPress: handleSubmit,
						loading: isSubmitting,
						disabled: isLocked || isSubmitting || processingMedia,
						icon: isLocked ? 'lock' : undefined,
					}}
				/>
			)}
		>
			{
				sections.map((section) => {
					const answerable = section.items.filter(item => item.type !== CHECKLIST_ITEM_TYPES.HEADING);
					const answeredCount = answerable.filter(item => isItemAnswered(item, answers[item.id])).length;
					return (
						<View key={section.key} {...readOnlyProps} style={sectionStyle}>
							{
								Boolean(section.title) && (
									<SectionTitle
										title={section.title}
										meta={answerable.length ? `${answeredCount} of ${answerable.length}` : undefined}
									/>
								)
							}
							{section.items.map(item => (
								<ChecklistItem
									key={item.id}
									item={item}
									value={answers[item.id]}
									comment={comments[item.id]}
									validationError={validationErrors[item.id]}
									isFlagged={flaggedItemIds.has(item.id)}
									onAnswer={setAnswerValue}
									onComment={setCommentValue}
									onAddPhoto={startPhotoCapture}
									onRemovePhoto={removePhoto}
									processingMedia={processingMedia}
									isCapturingPhoto={processingMedia && activePhotoItemIdRef.current === item.id}
									isReadOnly={Boolean(submitted)}
								/>
							))}
						</View>
					);
				})
			}
			{
				requiresManagerSignOff && (
					// The sign-off status stays at full strength on a submitted (greyed) checklist.
					<View style={styles.section}>
						<SectionTitle title="Manager sign-off" />
						<Card style={[styles.signOffCard, Boolean(managerSignOffError) && styles.itemError]}>
							<View style={styles.signOffStatus}>
								<View style={styles.signOffIcon}>
									<Feather
										name={signOffIcon}
										size={24}
										color={signOffIconColor}
									/>
								</View>
								<View style={styles.question}>
									<Text style={styles.questionLabel}>{signOffLabel}</Text>
									{Boolean(signOff?.note) && (
										<Text style={styles.description}>Note: {signOff.note}</Text>
									)}
									{!submitted && (
										<Text style={styles.description}>
											{isVehicleCheck
												? 'Ask a manager to inspect the vehicle, then select them. They get a notification to sign it off on their phone.'
												: 'Select the manager who will sign off this checklist. They get a notification to sign it off on their phone.'}
										</Text>
									)}
								</View>
							</View>
							{!submitted && (
								<ManagerSignOffSelect
									value={managerSignOff}
									onChange={(manager) => {
										setManagerSignOff(manager);
										setManagerSignOffError('');
									}}
									currentUserId={user?.id}
								/>
							)}
							{Boolean(managerSignOffError) && <Text style={styles.errorText}>{managerSignOffError}</Text>}
						</Card>
					</View>
				)
			}
		</Screen>
	);
};

const WrappedChecklistFormView = withMediaPicker(ChecklistFormView);

const ChecklistFormViewScreen = props => (
	<WrappedChecklistFormView
		s3Path="images/checklists/"
		allowMultipleSelection={true}
		allowMultipleCapture={true}
		collectCapturedMediaBatch={true}
		selectionLimit={MAX_PHOTOS_PER_ITEM}
		{...props}
	/>
);

export default ChecklistFormViewScreen;
